import { useCallback, useMemo, useState } from 'react';
import { Call, Line, VehicleMode } from './types';
import { Operator } from '../../hooks/useOperators';
import { getLocalTimeZone, now } from '@internationalized/date';

type VariantType = 'error' | 'warning' | 'success' | 'info';

type ValidationInput = {
  name?: string;
  mode?: VehicleMode;
  destinationDisplay?: string;
  operator?: Operator;
  line?: Line;
  calls: Call[];
};

export type ValidationFeedback = {
  feedback: string;
  variant: VariantType;
};

export type CallValidationResult = {
  [key in keyof Call]?: ValidationFeedback;
};

export type ValidationResult = {
  [key in keyof ValidationInput]?: ValidationFeedback | CallValidationResult[];
};

const isBefore = (a?: string, b?: string) => {
  if (a && b) {
    return new Date(a) < new Date(b);
  }
  return false;
};

export const runValidation = (input: ValidationInput): ValidationResult => {
  const tmpResult: ValidationResult = {};

  if (!input.name || input.name === '') {
    tmpResult.name = {
      feedback: 'Ekstraavgangen må ha et navn',
      variant: 'error',
    };
  }

  if (!input.mode) {
    tmpResult.mode = {
      feedback: 'Du må velge en vehicle mode',
      variant: 'error',
    };
  }

  if (!input.destinationDisplay || input.destinationDisplay === '') {
    tmpResult.destinationDisplay = {
      feedback: 'Ekstraavgangen må ha en destinasjonstekst',
      variant: 'error',
    };
  }

  if (!input.operator) {
    tmpResult.operator = {
      feedback: 'Ekstraavgangen må ha en gyldig operatør',
      variant: 'error',
    };
  }

  if (!input.line) {
    tmpResult.line = {
      feedback: 'Ekstraavgangen må ha en gyldig linje',
      variant: 'error',
    };
  }

  const callValidationResults: (CallValidationResult | undefined)[] =
    input.calls.map((call, i) => {
      const callResult: CallValidationResult = {};

      if (!call.quay) {
        callResult.quay = {
          feedback: 'Alle stopp må ha en plattform',
          variant: 'error',
        };
      }

      if (!call.alighting && !call.boarding) {
        callResult.alighting = {
          feedback: 'Alle stopp må tillate enten avstigning eller påstigning',
          variant: 'error',
        };
      }

      if (i !== 0 && !call.arrival) {
        callResult.arrival = {
          feedback: 'Stoppet må ha en ankomsttid',
          variant: 'error',
        };
      }

      if (i < input.calls.length - 1 && !call.departure) {
        callResult.departure = {
          feedback: 'Stoppet må ha en avgangstid',
          variant: 'error',
        };
      }

      if (
        call.arrival &&
        call.departure &&
        isBefore(call.departure, call.arrival)
      ) {
        callResult.departure = {
          feedback: 'Avgang kan ikke være før ankomst på samme stopp',
          variant: 'error',
        };
      }

      if (i !== 0 && isBefore(call.arrival, input.calls[i - 1].departure)) {
        callResult.arrival = {
          feedback: 'Ankomst er før avgang på forrige stopp',
          variant: 'error',
        };
      }

      if (
        i === input.calls.length - 1 &&
        call.arrival &&
        !isBefore(
          call.arrival,
          now(getLocalTimeZone()).add({ days: 7 }).toAbsoluteString(),
        )
      ) {
        callResult.arrival = {
          feedback: 'Ankomst på siste stopp kan ikke være mer enn 7 dager frem',
          variant: 'error',
        };
      }

      return Object.keys(callResult).length > 0 ? callResult : undefined;
    });

  if (callValidationResults.some((r) => r !== undefined)) {
    tmpResult.calls = callValidationResults as CallValidationResult[];
  }

  return tmpResult;
};

export const validateCallTimes = (
  calls: Call[],
): (CallValidationResult | undefined)[] => {
  return calls.map((call, i) => {
    const callResult: CallValidationResult = {};

    if (
      call.arrival &&
      call.departure &&
      isBefore(call.departure, call.arrival)
    ) {
      callResult.departure = {
        feedback: 'Avgang kan ikke være før ankomst på samme stopp',
        variant: 'error',
      };
    }

    if (
      i !== 0 &&
      call.arrival &&
      calls[i - 1].departure &&
      isBefore(call.arrival, calls[i - 1].departure)
    ) {
      callResult.arrival = {
        feedback: 'Ankomst er før avgang på forrige stopp',
        variant: 'error',
      };
    }

    return Object.keys(callResult).length > 0 ? callResult : undefined;
  });
};

export const useExtrajourneyValidation = (input: ValidationInput) => {
  const [submitResult, setSubmitResult] = useState<ValidationResult>({});

  // Live time-ordering errors — computed every render, no effect needed
  const liveCallErrors = useMemo(
    () => validateCallTimes(input.calls),
    // Stabilize on the serialized call times to avoid re-computing on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.calls.map((c) => `${c.arrival ?? ''}|${c.departure ?? ''}`).join()],
  );

  // Merge live call errors into submit result
  const result = useMemo((): ValidationResult => {
    const merged = { ...submitResult };
    const hasLiveErrors = liveCallErrors.some((r) => r !== undefined);
    if (!hasLiveErrors) return merged;

    const submitCalls = (merged.calls ?? []) as (
      CallValidationResult | undefined
    )[];
    const mergedCalls = input.calls.map((_, i) => {
      const combined = { ...submitCalls[i], ...liveCallErrors[i] };
      return Object.keys(combined).length > 0 ? combined : undefined;
    });
    if (mergedCalls.some((r) => r !== undefined)) {
      merged.calls = mergedCalls as CallValidationResult[];
    }
    return merged;
  }, [submitResult, liveCallErrors, input.calls]);

  const validate = useCallback(() => {
    const validationResult = runValidation(input);
    setSubmitResult(validationResult);
    return Object.keys(validationResult).length === 0;
  }, [input]);

  return { result, validate };
};
